import { Injectable, inject } from '@angular/core';

import { BehaviorSubject, firstValueFrom, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
/* Services */
import { MessageService } from '../../message.service';
import { TherapistChatService } from '@backend-client/services/therapist-chat.service';

/* Models */
import { ChatChannelModel, ChatMessageModel, ChatUserModel } from './model';
import { TubChatUserPresenceInfo } from '@backend-client/models/tub-chat-user-presence-info';

import { FirebaseFirestoreService } from '@firebase/firebase-firestore.service';
import { Timestamp, where, orderBy, limit } from 'firebase/firestore';

@Injectable({
  providedIn: 'root',
})
export class GoCoreChatService {
  private therapistChatService = inject(TherapistChatService);
  private messageService = inject(MessageService);
  private firebaseFirestore = inject(FirebaseFirestoreService);

  public readonly allSentMessages$: BehaviorSubject<ChatMessageModel[]> = new BehaviorSubject<ChatMessageModel[]>([]);
  public readonly messages$: BehaviorSubject<ChatMessageModel[]> = new BehaviorSubject<ChatMessageModel[]>([]);
  public readonly pendingMessages$: BehaviorSubject<ChatMessageModel[]> = new BehaviorSubject<ChatMessageModel[]>([]);
  public readonly chatChannels$: BehaviorSubject<ChatChannelModel[]> = new BehaviorSubject<ChatChannelModel[]>([]);
  public readonly typingIndicator$ = new BehaviorSubject<ChatUserModel[]>([]);
  public readonly selectedChatChannel$ = new BehaviorSubject<string>('');

  public readonly arePendingMessages$ = new BehaviorSubject<boolean>(false);
  public readonly errorSettingActiveChannel$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public loading$ = new BehaviorSubject<boolean>(true);

  // Loading Status
  public readonly chatLoadState$ = new BehaviorSubject<unknown>(null);
  public initialised$ = new BehaviorSubject<boolean>(false);

  /* Chat Client State */
  public typingNow: ChatUserModel[] = [];
  public channelUsers: TubChatUserPresenceInfo[] = [];
  public channelToActivate: string;

  private lastReadTimeSub: Subscription;

  public retryLoadSession(_channelId: string) {
    setTimeout(() => {
      if (!this.chatLoadState$.value) {
        void this.selectChannel(this.selectedChatChannel$.value);
      } else {
        this.messageService.showMessage('Retrying to fetch messages... please wait.');
        // No longer fetching messages from GoCore
        // this.loadMoreMessages(channelId);
      }
    }, 2000);
  }

  public static isLoadingActiveChannel = false;

  public async selectChannel(channelId: string): Promise<void> {
    this.loading$.next(true);

    // Clear typing indicators
    this.typingNow = [];
    this.typingIndicator$.next([]);

    // Clear all message subs
    this.allSentMessages$.next([]);
    this.messages$.next([]);
    this.pendingMessages$.next([]);

    await this.loadChatParticipants(channelId);
    GoCoreChatService.isLoadingActiveChannel = true;

    const channelString = 'PatientTherapistChat.';

    const concatName = `${channelString}${channelId}`;
    this.channelToActivate = concatName;

    GoCoreChatService.isLoadingActiveChannel = false;
  }

  private getPatientId(): string {
    return this.channelUsers.filter((u) => u.role === 'patient')[0]?.patientId;
  }

  private getPatientUserId(): string {
    return this.channelUsers.filter((u) => u.role === 'patient')[0]?.userId;
  }

  public async loadChatParticipants(selectedChannelId: string) {
    if (selectedChannelId == null) {
      return;
    }

    try {
      this.channelUsers = await firstValueFrom(
        this.therapistChatService.TherapistChatsControllerV2GetChatParticipants(selectedChannelId)
      );
      // Subscribe to the read receipts from firebase.
      // This is the earliest opportunity as we have just set the channel users, which the last read time logic is based
      // off.
      this.subscribeToReadReceipts();
    } catch {
      this.messageService.showMessage('Error: Failed to load chat participants');
    }
  }

  /**
   * Creates a subscription that listens for updates to the messages and patient last read time observables, and
   * combines those results to augment the messages with the correct hasBeenRead status.
   */
  /**
   * Unsubscribes from the Firestore read receipts subscription
   */
  public unsubscribeFromReadReceipts(): void {
    if (this.lastReadTimeSub) {
      this.lastReadTimeSub.unsubscribe();
      this.lastReadTimeSub = null;
    }
  }

  public subscribeToReadReceipts(): void {
    // Unsubscribe from previously set subscription to prevent memory leaks.
    this.unsubscribeFromReadReceipts();

    this.lastReadTimeSub = this.messages$
      .pipe(
        // Wait until messages are loaded - if there are no messages, there's no need to update read status
        filter((messages) => messages.length > 0),
        // For some reason, messages$ gets updated a LOT; this limits the performance impact
        distinctUntilChanged(),
        switchMap((messages) =>
          this.getLastReadTimeObservable().pipe(map((patientLastReadTime) => ({ messages, patientLastReadTime })))
        )
      )
      .subscribe(({ messages, patientLastReadTime }) => {
        // Loop through the messages calculating the read status.
        // We start at the latest message and iterate backwards; if a message is already flagged as read, it is safe to
        // infer that all messages from that point have been read too.
        for (let i = messages.length - 1; i >= 0; i--) {
          const message = messages[i];

          // Update message as read
          if (!message?.meta?.hasBeenRead) {
            const messageTime = message.timeId ? message.timeId : null;
            message.meta['hasBeenRead'] = patientLastReadTime >= messageTime;
          } else {
            break;
          }
        }

        this.messages$.next(messages);
      });
  }

  /**
   * Creates an observable that emits any read event from the current patient recorded in firestore.
   *
   * Queries the 'chat-events' Firestore collection for events where:
   *   - The 'subject' matches the specified patient document reference
   *   - The 'type' is 'read'
   *   - The 'author' matches the specified user document reference
   * Results are ordered by timestamp ('ts') descending, and only the most recent event is considered.
   */
  private getLastReadTimeObservable(): Observable<number> {
    return this.firebaseFirestore
      .collectionValueChanges<{
        ts?: Timestamp;
      }>('chat-events', [
        where('subject', '==', this.firebaseFirestore.doc(`patients/${this.getPatientId()}`)),
        where('type', '==', 'read'),
        where('author', 'in', [this.firebaseFirestore.doc(`users/${this.getPatientUserId()}`)]),
        orderBy('ts', 'desc'),
        limit(1),
      ])
      .pipe(
        map((val) => {
          return val[0]?.ts?.toMillis();
        })
      );
  }

  /**
   * Returns once GoCore has confirmed that there are no pending messages
   */
  public async waitForPendingMessagesToSend(): Promise<void> {
    await firstValueFrom(
      this.arePendingMessages$.pipe(
        filter((isPending) => !isPending), // Wait for no pending messages
        take(1) // Complete after the first matching value
      )
    );
    return;
  }
}
